import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { fromCallback, fromPromise } from 'xstate';

import { useQueryClient } from '@tanstack/react-query';
import {
  CareType, ContractData, getHigherEdOrg, getMatchPreference,
  getReferralByStudentId, getStudentByInviteToken, getStudentByStudentId, getSupportedAgeGroups,
  getUserContacts, getUserMatches, InsuranceMetaGrouping, MatchPreference
} from '@uplift/api';
import { STUDENT_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useStore } from '@uplift/hooks';
import { higherEdQueryKeys, useInsuranceCompaniesQuery } from '@uplift/queries';
import { ContactName, ProviderFilterTypeEnum, ProviderMedicalRole } from '@uplift/types';
import { useMachine } from '@xstate/react';

import { LoadActorReturnType, StudentOnboardingMachineContext } from '../types';
import { studentOnboardingMachine } from './machine';
import { getEligibility } from './utils';

type Params = {
  invite_token?: string;
} | {
  profile_data?: string;
};

type UseOnboardingMachineProps = {
  isExiting: boolean;
};

const getMoreCurrentUserData = async (userId: string, isLoggedIn: boolean): Promise<{
  matchPreference: MatchPreference;
  contextData: Partial<StudentOnboardingMachineContext>;
}> => {
  if (!isLoggedIn || !userId) {
    return {
      matchPreference: null,
      contextData: {},
    };
  }

  const matches = await getUserMatches({ userId, states: [ 'initial', 'active' ] });
  const initialMatch = matches.data.matches[0];
  const matchPreferences = initialMatch?.id ? await getMatchPreference(initialMatch?.id) : null;
  const contactsResponse = await getUserContacts(userId);
  const phoneVerified = contactsResponse.data.find(c => c.name === ContactName.mobile)?.verified;

  const selectedFilters: ProviderFilterTypeEnum[] = [];

  if (
    matchPreferences?.data?.faithIds?.length
    || matchPreferences?.data?.ethnicityIds?.length
    || matchPreferences?.data?.genderIds?.length
  ) {
    selectedFilters.push(ProviderFilterTypeEnum.Identity);
  }

  if (
    matchPreferences?.data?.communityExpertiseFaithIds?.length
    || matchPreferences?.data?.communityExpertiseEthnicityIds?.length
    || matchPreferences?.data?.communityExpertiseGeneralIds?.length
  ) {
    selectedFilters.push(ProviderFilterTypeEnum.Experience);
  }

  const ageGroupsResponse = await getSupportedAgeGroups();
  const ageGroup = ageGroupsResponse.ages.find(a => a.id === matchPreferences?.data?.ageGroupId);

  const contextData: Partial<StudentOnboardingMachineContext> = {
    matchId: initialMatch?.id,
    userId: userId,
    stateId: matchPreferences?.data?.locationState,
    seed: matchPreferences?.data?.seed,
    specialtyIds: matchPreferences?.data?.visitReasonIds ?? [],
    insuranceId: matchPreferences?.data?.insuranceMatch,
    modalityId: matchPreferences?.data?.modalityId,
    age: ageGroup ? { id: ageGroup.id, ageGroup: ageGroup.ageGroup } : null,
    identityFilters: {
      faithIds: matchPreferences?.data?.faithIds,
      ethnicityIds: matchPreferences?.data?.ethnicityIds,
      genderIds: matchPreferences?.data?.genderIds,
    },
    experienceFilters: {
      faithIds: matchPreferences?.data.communityExpertiseFaithIds,
      ethnicityIds: matchPreferences?.data.communityExpertiseEthnicityIds,
      communityIds: matchPreferences?.data.communityExpertiseGeneralIds,
    },
    showTheseProviderFilters: selectedFilters,
    phoneVerified: phoneVerified,
  };

  return {
    matchPreference: matchPreferences?.data,
    contextData: contextData,
  };
};

export const useStudentOnboardingMachine = (props: UseOnboardingMachineProps) => {
  const { data: partnerInsuranceCompanies = [] } = useInsuranceCompaniesQuery();
  const { authStore } = useStore();
  const me = useCurrentuser();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams.entries()) as Params;

  const skipSnapshot = 'invite_token' in params || authStore.isLoggedIn;
  const snapshot = useMemo(() => skipSnapshot ? null : JSON.parse(localStorage.getItem('student-matching')), [ skipSnapshot ]);

  const machine = useMachine(studentOnboardingMachine.provide({
    actors: {
      load: fromPromise(async (): Promise<LoadActorReturnType> => {
        const inviteToken = 'invite_token' in params ? params.invite_token : undefined;

        let careType: CareType;
        let orgId: string;
        let orgContract: ContractData;

        if (authStore.isLoggedIn) {
          const referral = await getReferralByStudentId(me?.id);
          const student = await getStudentByStudentId(me?.id);
          const org = await getHigherEdOrg(student?.data.orgId);

          queryClient.setQueryData([ higherEdQueryKeys.studentReferral(me.id) ], referral?.data);
          queryClient.setQueryData([ higherEdQueryKeys.student(me.id) ], student?.data);
          queryClient.setQueryData([ higherEdQueryKeys.org(student.data.orgId) ], org?.data);

          careType = referral?.data[0].careType;
          orgId = student?.data.orgId;
          orgContract = org?.data.contract;
        } else if (inviteToken) {
          const inviteResponse = await getStudentByInviteToken(inviteToken); // for new students

          queryClient.setQueryData([ 'student-invite', inviteToken ], inviteResponse.data);
          careType = inviteResponse.data.careType;
        } else {
          throw new Error('No care type found');
        }
        const { contextData } = await getMoreCurrentUserData(me.id, authStore.isLoggedIn);

        const context = {
          ...contextData,
          careType,
          isLoggedIn: authStore.isLoggedIn,
          inviteToken: inviteToken,
          orgId,
          orgContract,
        };
        const eligibility = getEligibility(context);

        history.replace(STUDENT_ONBOARDING_ROUTING_CONFIG.verifyPhone);

        return {
          ...context,
          medicalRole: eligibility.psychFirst ? ProviderMedicalRole.psychiatrist : ProviderMedicalRole.therapist,
        };
      }),
      handleHistoryPop: fromCallback(({ sendBack }) => {
        history.listen(() => {
          if (history.action === 'POP') {
            sendBack({ type: 'Back' });
          }
        });
      }),
      syncRouteActor: fromCallback(({ input }) => {
        history.push(input.route);
      })
    },
    guards: {
      isInsuranceMedicaid: ({ context, event }) => {
        let insuranceId = context.insuranceId;

        if (event.type === 'InsuranceInfo.Next' || event.type === 'Insurance.Next' || event.type === 'Matches.UpdateFilters') {
          insuranceId = event.data.insuranceId;
        }

        const insurance = partnerInsuranceCompanies.find(c => c.id === insuranceId);

        return insurance?.metaGrouping === InsuranceMetaGrouping.medicaid;
      }
    }
  }), {
    snapshot: snapshot,
  });

  const [ state, send, actor ] = machine;
  const persistedState = actor.getPersistedSnapshot();

  const { data: insuranceCompaniesForState } = useInsuranceCompaniesQuery({ stateId: state.context.stateId });
  const isPartnerInsurance = useMemo(() => (
    state.context.insuranceId && insuranceCompaniesForState
      ? insuranceCompaniesForState.some(company => company.id === state.context.insuranceId)
      : null
  ), [ state.context.insuranceId, insuranceCompaniesForState ]);

  useEffect(() => {
    if (props.isExiting) {
      return;
    }

    if (persistedState) {
      localStorage.setItem('student-matching', JSON.stringify(persistedState));
    }
  }, [ persistedState, props.isExiting ]);

  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }

    if (!authStore.loading && !me.isLoading) {
      triggeredRef.current = true;
      send({ type: 'Start' });
    }
  }, [ authStore.loading, me.isLoading, send ]);

  return { isPartnerInsurance, state, send };
};
